import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { driverService } from '../../../services/driverService';

const DriverSelectionModal = ({ isOpen, onClose, onSelectDriver, onSkip, vehicle }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadDrivers();
    }
  }, [isOpen]);

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const data = await driverService?.getAvailable();
      setDrivers(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDriver = () => {
    const driver = drivers?.find(d => d?.id === selectedDriverId);
    if (driver) {
      onSelectDriver(driver);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-bold text-white">Sélectionner un Chauffeur</h2>
            <p className="text-sm text-slate-400 mt-1">
              Optionnel - Vous pouvez conduire vous-même ou choisir un chauffeur professionnel
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Vehicle Info */}
        <div className="p-6 bg-slate-800/50 border-b border-white/10">
          <div className="flex items-center gap-4">
            <Image
              src={vehicle?.image}
              alt={vehicle?.imageAlt}
              className="w-24 h-16 rounded-lg object-cover"
            />
            <div>
              <h3 className="font-semibold text-white">{vehicle?.name}</h3>
              <p className="text-sm text-slate-400">
                {vehicle?.dailyRate?.toLocaleString()} CDF / jour
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent" />
              <p className="mt-4 text-slate-400">Chargement des chauffeurs...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
              <p className="text-red-400">Erreur: {error}</p>
            </div>
          ) : drivers?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="text-slate-400 mx-auto mb-4" />
              <p className="text-slate-400">Aucun chauffeur disponible</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {drivers?.map((driver) => (
                <div
                  key={driver?.id}
                  onClick={() => setSelectedDriverId(driver?.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedDriverId === driver?.id
                      ? 'border-blue-500 bg-blue-500/5' :'border-white/10 hover:border-blue-500/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <Image
                      src={driver?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400'}
                      alt={driver?.avatarAlt || `Photo de ${driver?.fullName}`}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-white">{driver?.fullName}</h4>
                        {selectedDriverId === driver?.id && (
                          <Icon name="CheckCircle2" size={20} className="text-blue-400" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        <Icon name="Star" size={14} className="text-amber-400 fill-amber-400" />
                        <span className="text-sm font-medium text-white">{driver?.rating}</span>
                        <span className="text-xs text-slate-400">
                          ({driver?.totalTrips} voyages)
                        </span>
                      </div>
                      <div className="space-y-1 mb-3">
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <Icon name="Award" size={12} />
                          <span>{driver?.experienceYears} ans d'expérience</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <Icon name="Languages" size={12} />
                          <span>{driver?.languages?.join(', ')}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-blue-400">
                          {driver?.dailyRate?.toLocaleString()} CDF / jour
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 bg-slate-800/30">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              fullWidth
              iconName="X"
              iconPosition="left"
              onClick={onSkip}
            >
              Conduire Moi-Même
            </Button>
            <Button
              variant="default"
              fullWidth
              iconName="UserCheck"
              iconPosition="left"
              onClick={handleSelectDriver}
              disabled={!selectedDriverId}
            >
              Continuer avec ce Chauffeur
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverSelectionModal;
